import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Clock, FlaskConical, Users, Home, AlertTriangle,
  CheckCircle, DoorOpen, DollarSign, LogOut, Target, ChevronRight,
  ClipboardCheck, Loader2, TrendingUp, TrendingDown, Minus
} from 'lucide-react';

const METRIC_COLORS = {
  teal: 'bg-teal-50 text-teal-600',
  violet: 'bg-violet-50 text-violet-600',
  sky: 'bg-sky-50 text-sky-600',
  amber: 'bg-amber-50 text-amber-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  red: 'bg-red-50 text-red-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  orange: 'bg-orange-50 text-orange-600',
  slate: 'bg-slate-100 text-slate-600',
};

const METRIC_CONFIG = [
  { key: 'curfew', label: 'Curfew', icon: Clock, color: 'teal' },
  { key: 'drug_testing', label: 'Drug Testing', icon: FlaskConical, color: 'violet' },
  { key: 'meetings', label: 'House Meetings', icon: Users, color: 'sky' },
  { key: 'house_rules', label: 'House Rules', icon: Home, color: 'amber' },
  { key: 'inspections', label: 'Room Inspections', icon: DoorOpen, color: 'emerald' },
  { key: 'incidents', label: 'Incidents', icon: AlertTriangle, color: 'red' },
  { key: 'program', label: 'Program Activity', icon: Target, color: 'indigo' },
  { key: 'rent', label: 'Rent/Fees', icon: DollarSign, color: 'orange' },
  { key: 'moveout', label: 'Move-Out Plan', icon: LogOut, color: 'slate' },
];

function ScoreRing({ score, size = 64 }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : score >= 40 ? '#f97316' : '#ef4444';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="5" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-700" />
      </svg>
      <span className="absolute text-sm font-bold" style={{ color }}>{Math.round(score)}</span>
    </div>
  );
}

function MetricBar({ metric, score }) {
  const config = METRIC_CONFIG.find(m => m.key === metric);
  if (!config) return null;
  const Icon = config.icon;
  const rate = score?.rate ?? 0;
  const barColor = rate >= 80 ? 'bg-emerald-500' : rate >= 60 ? 'bg-amber-500' : rate >= 40 ? 'bg-orange-500' : 'bg-red-500';
  const iconClasses = METRIC_COLORS[config.color] || 'bg-slate-100 text-slate-600';
  const [bgClass, textClass] = iconClasses.split(' ');

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`metric-${metric}`}>
      <div className={`w-7 h-7 rounded-lg ${bgClass} flex items-center justify-center shrink-0`}>
        <Icon size={14} className={textClass} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-xs font-medium">{config.label}</span>
          <span className="text-xs text-muted-foreground">{Math.round(rate)}%<span className="text-[9px] ml-1 opacity-50">w{score?.weight}</span></span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${rate}%` }} />
        </div>
      </div>
    </div>
  );
}

export default function ComplianceDashboardPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [houses, setHouses] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState('all');
  const [selectedResident, setSelectedResident] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = selectedHouse !== 'all' ? `?house_id=${selectedHouse}` : '';
      const [dashRes, housesRes] = await Promise.all([
        api.get(`/housing/compliance-dashboard${query}`).catch(() => ({ data: null })),
        api.get('/housing/entity/our_square/dashboard').catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      if (Array.isArray(housesRes.data)) setHouses(housesRes.data);
      else if (housesRes.data?.houses) setHouses(housesRes.data.houses);
    } catch {
      toast.error('Failed to load compliance dashboard');
    }
    setLoading(false);
  }, [selectedHouse]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;

  const summary = data?.summary || {};
  const residents = data?.residents || [];
  const atRisk = data?.at_risk || [];

  return (
    <div className="space-y-6" data-testid="compliance-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Shield className="text-teal-600" size={22} /> 10-Point Compliance Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Real-time compliance tracking across all Our Square properties</p>
        </div>
        <Select value={selectedHouse} onValueChange={setSelectedHouse}>
          <SelectTrigger className="w-[220px] rounded-full" data-testid="house-filter">
            <SelectValue placeholder="All Properties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4 text-center">
            <ScoreRing score={summary.average_score || 0} size={56} />
            <p className="text-xs text-muted-foreground mt-1">Average Score</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{summary.compliant_count || 0}</p>
            <p className="text-xs text-muted-foreground">Compliant</p>
            <p className="text-[10px] text-emerald-600">Score 70+</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{summary.at_risk_count || 0}</p>
            <p className="text-xs text-muted-foreground">At Risk</p>
            <p className="text-[10px] text-red-600">Score below 50</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{summary.total_residents || 0}</p>
            <p className="text-xs text-muted-foreground">Total Residents</p>
            <p className="text-[10px] text-muted-foreground">{summary.houses_count || 0} properties</p>
          </CardContent>
        </Card>
      </div>

      {/* At Risk Alert */}
      {atRisk.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl bg-red-50 border-l-4 border-l-red-500" data-testid="at-risk-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-900">{atRisk.length} Resident{atRisk.length > 1 ? 's' : ''} At Risk (Below 50)</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {atRisk.map(r => (
                    <button key={r.resident_id} type="button" onClick={() => setSelectedResident(r)}
                      className="flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full text-xs font-medium text-red-800 border border-red-200 hover:bg-red-100 transition-colors"
                      data-testid={`at-risk-${r.resident_id}`}
                    >
                      <span className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center text-[10px] font-bold text-red-700">{Math.round(r.overall_score)}</span>
                      {r.full_name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 10-Point Metric Overview */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><ClipboardCheck size={14} /> 10-Point Compliance Metrics (Weights)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-1">
            {METRIC_CONFIG.map(config => {
              const avgRate = residents.length
                ? Math.round(residents.reduce((s, r) => s + (r.scores?.[config.key]?.rate || 0), 0) / residents.length)
                : 0;
              return (
                <div key={config.key} className="flex items-center gap-2 p-2 rounded-lg">
                  <config.icon size={14} className="text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs">{config.label}</span>
                      <span className="text-xs font-medium">{avgRate}%</span>
                    </div>
                    <Progress value={avgRate} className="h-1 mt-0.5" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Resident Compliance Table */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Resident Compliance Scores</CardTitle>
        </CardHeader>
        <CardContent>
          {residents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No residents found. Add residents to properties to track compliance.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {residents.map(r => (
                <button
                  key={r.resident_id}
                  type="button"
                  className="w-full flex items-center justify-between p-3 rounded-xl border hover:border-teal-300 hover:bg-teal-50/30 transition-all text-left"
                  onClick={() => setSelectedResident(r)}
                  data-testid={`resident-score-${r.resident_id}`}
                >
                  <div className="flex items-center gap-3">
                    <ScoreRing score={r.overall_score} size={42} />
                    <div>
                      <p className="text-sm font-medium">{r.full_name}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                        <span>{r.house_name}</span>
                        <Badge variant="outline" className="text-[9px] capitalize">{r.program_phase?.replace(/_/g, ' ')}</Badge>
                        {r.overall_score >= 80 ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]"><TrendingUp size={9} className="mr-0.5" /> Excellent</Badge>
                        ) : r.overall_score >= 60 ? (
                          <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px]"><Minus size={9} className="mr-0.5" /> Fair</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 border-0 text-[9px]"><TrendingDown size={9} className="mr-0.5" /> At Risk</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground/40 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resident Detail Dialog */}
      <Dialog open={!!selectedResident} onOpenChange={(o) => !o && setSelectedResident(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedResident && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <ScoreRing score={selectedResident.overall_score} size={48} />
                  <div>
                    <span>{selectedResident.full_name}</span>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5">{selectedResident.house_name}</p>
                  </div>
                </DialogTitle>
                <DialogDescription>10-point compliance breakdown with weighted scores</DialogDescription>
              </DialogHeader>
              <div className="space-y-1 mt-2">
                {METRIC_CONFIG.map(config => (
                  <MetricBar key={config.key} metric={config.key} score={selectedResident.scores?.[config.key]} />
                ))}
              </div>
              <div className="mt-3 p-3 rounded-xl bg-slate-50 text-xs space-y-1">
                <p><strong>Weighted Score Breakdown:</strong></p>
                {METRIC_CONFIG.map(config => {
                  const s = selectedResident.scores?.[config.key];
                  if (!s) return null;
                  const weighted = ((s.rate / 100) * s.weight).toFixed(1);
                  return <p key={config.key} className="text-muted-foreground">{config.label}: {Math.round(s.rate)}% x {s.weight}w = <strong>{weighted}</strong></p>;
                })}
                <p className="pt-1 border-t font-semibold">Total: {selectedResident.overall_score}/100</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
